// src/pages/GetStarted.jsx
// Streamlined onboarding for vibe coders (Lovable, v0, Replit users)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Check, Copy, ExternalLink, ArrowRight, Zap, Key, User, Webhook } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { calendarService } from '../services/calendarService';
import styles from './GetStarted.module.css';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

// Inline Google logo SVG (brand asset — no token equivalent)
function GoogleLogo() {
  return (
    <svg className={styles.googleLogo} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

export default function GetStarted() {
  const { user, loading: authLoading, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [publishableKey, setPublishableKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [checkingCalendar, setCheckingCalendar] = useState(false);

  useEffect(() => {
    if (user) { setStep(2); checkCalendarConnection(); }
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected') === 'true' || params.get('calendar_connected') === 'true') {
      setCalendarConnected(true); setStep(3);
      window.history.replaceState({}, '', '/get-started');
      getPublishableKey();
    }
  }, []);

  async function checkCalendarConnection() {
    setCheckingCalendar(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const response = await fetch(`${API_URL}/v1/calendars/connections`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.connections?.length > 0) { setCalendarConnected(true); setStep(3); getPublishableKey(); }
      }
    } catch (error) {
      console.error('Error checking calendar:', error);
    } finally {
      setCheckingCalendar(false);
    }
  }

  async function getPublishableKey() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const response = await fetch(`${API_URL}/v1/api-keys/publishable`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'My App' })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.api_key?.key) setPublishableKey(data.api_key.key);
        else if (data.api_key?.key_prefix) setPublishableKey(data.api_key.key_prefix);
      }
    } catch (error) {
      console.error('Error getting publishable key:', error);
    }
  }

  async function handleGoogleSignUp() {
    setLoading(true);
    try {
      localStorage.setItem('pendingRole', 'business');
      localStorage.setItem('vibeCoderOnboarding', 'true');
      const { error } = await signInWithGoogle();
      if (error) toast.error(error.message);
    } catch (error) {
      toast.error('Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  }

  async function handleConnectGoogle() {
    setLoading(true);
    try {
      if (!user) { toast.error('Please sign in first'); return; }
      localStorage.setItem('vibeCoderOnboarding', 'true');
      window.location.href = await calendarService.getGoogleAuthUrl();
    } catch (error) {
      console.error('Error connecting calendar:', error);
      toast.error('Failed to connect calendar');
      setLoading(false);
    }
  }

  function copyCode() {
    navigator.clipboard.writeText(getCodeSnippet());
    setCopied(true);
    toast.success('Code copied!');
    setTimeout(() => setCopied(false), 2000);
  }

  function getCodeSnippet() {
    const key = publishableKey || 'pk_live_your_key_here';
    return `import { Vennio } from '@vennio/sdk'

const vennio = new Vennio('${key}')

// Get your available time slots
const slots = await vennio.getSlots({
  duration: 30, // minutes
  days: 14      // look ahead
})

// Book a slot
const booking = await vennio.book(slots[0], {
  email: 'customer@example.com',
  name: 'Jane Smith'
})

console.log('Booked!', booking.id)`;
  }

  if (authLoading) {
    return (
      <div className={styles.loadingPage}>
        <div className={styles.spinner} aria-label="Loading" />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.navbar}>
        <div className={styles.navInner}>
          <div className={styles.navLogo}>
            <div className={styles.navLogoIcon}>
              <Calendar className={styles.navLogoCalendar} size={20} aria-hidden="true" />
            </div>
            <span className={styles.navBrand}>Vennio</span>
          </div>
          {user && (
            <button onClick={() => navigate('/dashboard')} className={styles.navLink}>
              Go to Dashboard
            </button>
          )}
        </div>
      </div>

      <div className={styles.body}>
        {/* Hero */}
        <div className={styles.hero}>
          <div className={styles.heroBadge}>
            <Zap size={14} aria-hidden="true" />
            For Lovable, v0, Replit builders
          </div>
          <h1 className={styles.heroTitle}>Add scheduling in 5 minutes</h1>
          <p className={styles.heroSubtitle}>Connect your calendar, copy the code, done.</p>
        </div>

        {/* Progress */}
        <div className={styles.progress}>
          {[1, 2, 3].map((s) => (
            <div key={s} className={styles.progressItem}>
              <div className={`${styles.progressDot}${step > s ? ` ${styles.progressDotDone}` : step === s ? ` ${styles.progressDotActive}` : ''}`}>
                {step > s ? <Check size={16} aria-hidden="true" /> : s}
              </div>
              {s < 3 && <div className={`${styles.progressLine}${step > s ? ` ${styles.progressLineDone}` : ''}`} />}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className={styles.card}>
          {/* Step 1: Sign up */}
          {step === 1 && (
            <div className={styles.stepCenter}>
              <h2 className={styles.stepTitle}>Create your account</h2>
              <p className={styles.stepSubtitle}>Sign up with Google to get started instantly</p>
              <button onClick={handleGoogleSignUp} disabled={loading} className={styles.googleBtn}>
                <GoogleLogo />
                {loading ? 'Signing up...' : 'Continue with Google'}
              </button>
              <p className={styles.stepNote}>
                Already have an account?{' '}
                <button onClick={() => navigate('/auth')} className={styles.inlineLink}>Sign in</button>
              </p>
            </div>
          )}

          {/* Step 2: Connect calendar */}
          {step === 2 && (
            <div className={styles.stepCenter}>
              <h2 className={styles.stepTitle}>Connect your calendar</h2>
              <p className={styles.stepSubtitle}>We'll sync your availability so customers can book when you're free</p>
              {checkingCalendar ? (
                <div className={styles.checkingWrap}>
                  <div className={styles.spinner} aria-label="Checking calendar" />
                </div>
              ) : (
                <>
                  <button onClick={handleConnectGoogle} disabled={loading} className={styles.googleBtn}>
                    <GoogleLogo />
                    {loading ? 'Connecting...' : 'Connect Google Calendar'}
                  </button>
                  <button onClick={() => { setStep(3); getPublishableKey(); }} className={styles.skipBtn}>
                    Skip for now (you can connect later)
                  </button>
                </>
              )}
            </div>
          )}

          {/* Step 3: Get code */}
          {step === 3 && (
            <div>
              <div className={styles.stepCenter}>
                <h2 className={styles.stepTitle}>You're ready to go!</h2>
                <p className={styles.stepSubtitle}>Copy this code into your app</p>
              </div>

              {/* Integration credentials */}
              <div className={styles.credentialsPanel}>
                <h3 className={styles.credentialsTitle}>Your Integration Credentials</h3>
                <div className={styles.credentialsList}>
                  {user && (
                    <div className={styles.credentialItem}>
                      <div className={styles.credentialIcon}><User size={14} aria-hidden="true" /></div>
                      <div className={styles.credentialInfo}>
                        <span className={styles.credentialLabel}>Business ID</span>
                        <code className={styles.credentialValue}>{user.id}</code>
                      </div>
                      <button
                        onClick={() => { navigator.clipboard.writeText(user.id); toast.success('Business ID copied!'); }}
                        className={styles.credentialCopyBtn}
                      >
                        <Copy size={14} aria-hidden="true" />
                      </button>
                    </div>
                  )}
                  {publishableKey && (
                    <div className={styles.credentialItem}>
                      <div className={styles.credentialIcon}><Key size={14} aria-hidden="true" /></div>
                      <div className={styles.credentialInfo}>
                        <span className={styles.credentialLabel}>API Key</span>
                        <code className={styles.credentialValue}>{publishableKey}</code>
                      </div>
                      <button
                        onClick={() => { navigator.clipboard.writeText(publishableKey); toast.success('API Key copied!'); }}
                        className={styles.credentialCopyBtn}
                      >
                        <Copy size={14} aria-hidden="true" />
                      </button>
                    </div>
                  )}
                  <div className={styles.credentialItem}>
                    <div className={styles.credentialIcon}><Webhook size={14} aria-hidden="true" /></div>
                    <div className={styles.credentialInfo}>
                      <span className={styles.credentialLabel}>Webhook Secret</span>
                      <span className={styles.credentialHint}>Created when you set up a webhook</span>
                    </div>
                    <button
                      onClick={() => navigate('/dashboard')}
                      className={styles.credentialSetupBtn}
                    >
                      Set up
                    </button>
                  </div>
                </div>
                {publishableKey?.includes('...') && (
                  <p className={styles.credentialsNote}>
                    Already have a key? Create a new one in{' '}
                    <button onClick={() => navigate('/api-keys')} className={styles.inlineLink}>API Keys</button>
                  </p>
                )}
              </div>

              <div className={styles.codeWrap}>
                <button onClick={copyCode} className={styles.copyBtn}>
                  {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
                <pre className={styles.codeBlock}><code>{getCodeSnippet()}</code></pre>
              </div>

              <div className={styles.installBox}>
                <p className={styles.installLabel}>Install the SDK:</p>
                <div className={styles.installRow}>
                  <code className={styles.installCmd}>npm install @vennio/sdk</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText('npm install @vennio/sdk'); toast.success('Copied!'); }}
                    className={styles.installCopyBtn}
                  >
                    <Copy size={16} aria-hidden="true" />
                  </button>
                </div>
              </div>

              <div className={styles.nextSteps}>
                <button onClick={() => navigate('/dashboard')} className={styles.dashBtn}>
                  Go to Dashboard
                  <ArrowRight size={16} aria-hidden="true" />
                </button>
                <a href="/developers" className={styles.docsBtn}>
                  View Documentation
                  <ExternalLink size={16} aria-hidden="true" />
                </a>
              </div>

              {!calendarConnected && (
                <div className={styles.calendarReminder}>
                  <p className={styles.reminderText}>
                    <strong>Reminder:</strong> Connect your calendar to enable real availability.{' '}
                    <button onClick={handleConnectGoogle} className={styles.inlineLink}>Connect now</button>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Trust indicators */}
        <div className={styles.trust}>
          <p className={styles.trustLabel}>Built for modern app builders</p>
          <div className={styles.trustLogos}>
            {['Lovable', 'v0', 'Replit', 'Cursor'].map(name => (
              <span key={name} className={styles.trustLogo}>{name}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
